import React, { useState, useEffect, useMemo } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { dashboardApi } from '../../services/dashboardApi';
import { Settings, BookOpen } from 'lucide-react';

import CashbookDetails from './CashbookDetails';
import AddEntryModal from './AddEntryModal';
import EditEntryModal from './EditEntryModal';
import CashbookReports from './CashbookReports';
import CashbookReportDay from './CashbookReportDay';

const CashbookHome = () => {
  const { t } = useLang();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_balance: 0,
    cash_in_hand: 0,
    online_balance: 0,
    today_balance: 0,
    today_in: 0,
    today_out: 0
  });

  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [entryType, setEntryType] = useState('in');
  const [selectedReportDate, setSelectedReportDate] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await dashboardApi.getCashbookData();

      if (response?.success && response.data) {
        setSummary(response.data.summary || summary);

        let allEntries = [];
        const entriesData = response.data.entries || [];

        if (entriesData && typeof entriesData === 'object' && !Array.isArray(entriesData)) {
          Object.values(entriesData).forEach(dayEntries => {
            if (Array.isArray(dayEntries)) {
              allEntries = [...allEntries, ...dayEntries];
            }
          });
        } else if (Array.isArray(entriesData)) {
          allEntries = entriesData;
        }

        setEntries(allEntries);

        // Update selected entry if it exists
        if (selectedEntry) {
          const updated = allEntries.find(e => e.id === selectedEntry.id);
          if (updated) setSelectedEntry(updated);
        }
      }
    } catch (error) {
      console.error('Failed to fetch cashbook data:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedEntries = useMemo(() => {
    const groups = {};

    entries.forEach(entry => {
      const d = new Date(entry.date || entry.entry_date);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(entry);
    });

    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const formatCurrency = (val) => {
    const n = parseFloat(val || 0);
    return '₹ ' + new Intl.NumberFormat('en-IN').format(Math.abs(n));
  };

  const handleDelete = async (id) => {
    if (window.confirm(t(LangKeys.delete_confirm) || "Are you sure you want to delete this entry?")) {
      try {
        await dashboardApi.deleteCashbookEntry(id);
        setSelectedEntry(null);
        fetchData();
      } catch (error) {
        alert("Failed to delete entry");
      }
    }
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="print-main-wrapper" style={styles.pageWrapper}>
      <div style={styles.mainContentArea}>
        {/* LEFT PANEL - List & Summary */}
        <div className="no-print" style={styles.leftPanel}>
          <div style={styles.scrollArea}>
            <div style={styles.scrollContent}>
              {/* Summary Card */}
              <div style={styles.summarySection}>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryTop}>
                    <div style={styles.summaryBlock}>
                      <span style={styles.summaryLabel}>
                        {t(LangKeys.total_balance_cashbook)}
                      </span>
                      <h2 style={{ ...styles.summaryValue, color: '#16A34A' }}>
                        {formatCurrency(summary.total_balance)}
                      </h2>
                      <div style={styles.miniStats}>
                        <div style={styles.miniStat}>
                          <span style={styles.dot} /> {t(LangKeys.cash)}:{' '}
                          <strong>{formatCurrency(summary.cash_in_hand)}</strong>
                        </div>
                        <div style={{ ...styles.miniStat, marginTop: '2px' }}>
                          <span style={{ ...styles.dot, backgroundColor: '#3B82F6' }} />
                          {t(LangKeys.online)}:{' '}
                          <strong>{formatCurrency(summary.online_balance)}</strong>
                        </div>
                      </div>
                    </div>

                    <div style={styles.summaryDivider} />

                    <div style={styles.summaryBlock}>
                      <span style={styles.summaryLabel}>
                        {t(LangKeys.today_balance)}
                      </span>
                      <h2 style={{ ...styles.summaryValue, color: '#16A34A' }}>
                        {formatCurrency(summary.today_balance)}
                      </h2>
                      <div style={styles.todayMeta}>
                        <span style={{ color: '#16A34A' }}>
                          ↑ {formatCurrency(summary.today_in)}
                        </span>
                        <span style={{ color: '#DC2626', marginLeft: '8px' }}>
                          ↓ {formatCurrency(summary.today_out)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    style={styles.summaryFooter}
                    onClick={() => setShowReports(true)}
                  >
                    📄 {t(LangKeys.view_report)}
                  </div>
                </div>
              </div>

              {/* Entries List */}
              <div style={styles.listContainer}>
                <div style={styles.listHeader}>
                  <span style={styles.listTitle}>
                    {entries.length} {t(LangKeys.entries)}
                  </span>
                  <Settings size={16} color="#94A3B8" />
                </div>

                {entries.length === 0 ? (
                  <div style={styles.leftEmptyState}>
                    <div style={styles.leftEmptyIcon}>
                      <BookOpen size={48} color="#CBD5E1" />
                    </div>
                    <h3 style={styles.leftEmptyTitle}>
                      {t(LangKeys.no_entries_yet)}
                    </h3>
                  </div>
                ) : (
                  groupedEntries.map(([date, dayEntries]) => (
                  <div key={date} style={styles.dateGroup}>
                    <div style={styles.dateHeader}>
                      {new Date(date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>

                    {dayEntries.map(entry => {
                      const isIn = (entry.type || entry.entry_type) === 'in';
                      const color = isIn ? '#16A34A' : '#DC2626';

                      return (
                        <div
                          key={entry.id}
                          style={{
                            ...styles.entryRow,
                            backgroundColor: selectedEntry?.id === entry.id ? '#F0F9FF' : '#FFF',
                            borderLeft: `3px solid ${color}`
                          }}
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowReports(false);
                          }}
                        >
                          <div style={styles.entryInfo}>
                            <div style={{ ...styles.entryType, color }}>
                              {isIn ? t(LangKeys.in_entry).toUpperCase() : t(LangKeys.out_entry).toUpperCase()}
                            </div>
                            <div style={styles.entryMeta}>
                              {(entry.mode || entry.payment_mode) === 'cash'
                                ? t(LangKeys.cash)
                                : t(LangKeys.online)
                              } • {' '}
                              {new Date(entry.date || entry.entry_date).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            {entry.note && (
                              <div style={styles.entryNote}>{entry.note}</div>
                            )}
                          </div>

                          <div style={{ ...styles.entryAmount, color }}>
                            {isIn ? '+' : '−'} {formatCurrency(entry.amount)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )))}
              </div>
            </div>
          </div>

          {/* Bottom Action Buttons */}
          <div style={styles.bottomBar}>
            <button
              style={{ ...styles.actionBtn, backgroundColor: '#DC2626' }}
              onClick={() => {
                setEntryType('out');
                setShowAddModal(true);
              }}
            >
              − {t(LangKeys.out_entry)}
            </button>

            <button
              style={{ ...styles.actionBtn, backgroundColor: '#16A34A' }}
              onClick={() => {
                setEntryType('in');
                setShowAddModal(true);
              }}
            >
              + {t(LangKeys.in_entry)}
            </button>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="print-right-column" style={styles.rightPanel}>
          {selectedReportDate ? (
            <CashbookReportDay
              date={selectedReportDate}
              onClose={() => setSelectedReportDate(null)}
            />
          ) : showReports ? (
            <CashbookReports
              onClose={() => setShowReports(false)}
              initialEntries={entries}
              initialSummary={summary}
              onDayClick={(date) => setSelectedReportDate(date)}
            />
          ) : selectedEntry ? (
            <CashbookDetails
              entry={selectedEntry}
              onDelete={handleDelete}
              onClose={() => setSelectedEntry(null)}
              onEdit={() => setShowEditModal(true)}
            />
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIconCircle}>
                <div style={styles.emptyIconInner}>₹</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddEntryModal
          type={entryType}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
        />
      )}

      {showEditModal && selectedEntry && (
        <EditEntryModal
          entry={selectedEntry}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

const styles = {
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
    backgroundColor: '#F3F4F6'
  },
  mainContentArea: {
    flex: 1,
    display: 'flex',
    height: '100%'
  },
  leftPanel: {
    width: '400px',
    backgroundColor: '#FFF',
    borderRight: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin'
  },
  scrollContent: {
    padding: '16px'
  },
  rightPanel: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },

  summarySection: { marginBottom: '16px' },
  summaryCard: {
    backgroundColor: '#FFF',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
    overflow: 'hidden'
  },
  summaryTop: {
    display: 'flex',
    padding: '16px'
  },
  summaryBlock: { flex: 1 },
  summaryDivider: {
    width: '1px',
    backgroundColor: '#E5E7EB',
    margin: '0 16px'
  },
  summaryLabel: {
    fontSize: '10px',
    color: '#9CA3AF',
    fontWeight: '700',
    textTransform: 'uppercase'
  },
  summaryValue: {
    fontSize: '20px',
    fontWeight: '800',
    margin: '4px 0'
  },
  miniStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  miniStat: {
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  dot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: '#16A34A'
  },
  todayMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontSize: '11px',
    fontWeight: '700'
  },
  summaryFooter: {
    backgroundColor: '#FACC15',
    padding: '8px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#1E3A8A',
    cursor: 'pointer'
  },

  listContainer: { padding: '0 2px' },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  listTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#6B7280'
  },
  dateGroup: { marginBottom: '12px' },
  dateHeader: {
    fontSize: '10px',
    fontWeight: '800',
    color: '#9CA3AF',
    textTransform: 'uppercase',
    marginBottom: '6px'
  },
  entryRow: {
    backgroundColor: '#FFF',
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
    cursor: 'pointer',
    border: '1px solid #F3F4F6'
  },
  entryInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  entryType: {
    fontSize: '10px',
    fontWeight: '800'
  },
  entryMeta: {
    fontSize: '10px',
    color: '#9CA3AF'
  },
  entryNote: {
    fontSize: '12px',
    color: '#1E293B',
    fontWeight: '500',
    marginTop: '2px'
  },
  entryAmount: {
    fontSize: '14px',
    fontWeight: '700'
  },

  bottomBar: {
    padding: '12px',
    backgroundColor: '#FFF',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    gap: '10px'
  },
  actionBtn: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    color: '#FFF',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer'
  },

  emptyState: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  emptyIconCircle: {
    width: '120px',
    height: '120px',
    borderRadius: '40px',
    backgroundColor: '#FFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
  },
  emptyIconInner: {
    fontSize: '64px',
    fontWeight: '900',
    color: '#F1F5F9'
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  leftEmptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  leftEmptyIcon: {
    marginBottom: '16px',
    padding: '16px',
    borderRadius: '20px',
    background: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftEmptyTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#475569',
    margin: 0,
  }
};

export default CashbookHome;